"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type MutableRefObject,
  type Ref,
  type RefCallback,
} from "react";

type InpSafeSubmit = (
  e?: FormEvent | MouseEvent | KeyboardEvent | HTMLElement,
) => void | Promise<void>;

const formSubmitRegistry = new WeakMap<
  HTMLFormElement,
  (eventOrElement?: FormEvent | MouseEvent | KeyboardEvent | HTMLElement) => void
>();

export const InpSafeFormContext = createContext<
  ((eventOrElement?: FormEvent | MouseEvent | KeyboardEvent | HTMLElement) => void | Promise<void>) | null
>(null);

export function useInpSafeFormSubmit(): ((eventOrElement?: FormEvent | MouseEvent | KeyboardEvent | HTMLElement) => void | Promise<void>) | null {
  return useContext(InpSafeFormContext);
}

export function registerInpSafeFormSubmit(
  form: HTMLFormElement | null,
  submit: (eventOrElement?: FormEvent | MouseEvent | KeyboardEvent | HTMLElement) => void,
): void {
  if (form) {
    formSubmitRegistry.set(form, submit);
  }
}

/** Invoke registered submit from SubmitButton / type="button" controls. */
export function triggerInpSafeFormSubmit(
  button: HTMLButtonElement,
  eventOrElement?: FormEvent | MouseEvent | KeyboardEvent | HTMLElement,
): void {
  const form = button.form ?? button.closest("form");
  if (!form) return;
  formSubmitRegistry.get(form)?.(eventOrElement ?? button);
}

/** Click handler for raw <button> inside InpSafeForm (replaces type="submit"). */
export function onInpSafeButtonClick(
  e: MouseEvent<HTMLButtonElement>,
): void {
  e.preventDefault();
  e.stopPropagation();
  triggerInpSafeFormSubmit(e.currentTarget, e);
}

export function mergeRefs<T>(
  ...refs: Array<Ref<T> | undefined | null>
): RefCallback<T> {
  return (value: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") {
        ref(value);
      } else {
        (ref as MutableRefObject<T | null>).current = value;
      }
    }
  };
}

export function inpSafeFormProps(submit: InpSafeSubmit) {
  return {
    onSubmit: (e: FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onKeyDown: (e: KeyboardEvent<HTMLFormElement>) => {
      if (e.key !== "Enter" || e.defaultPrevented) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "TEXTAREA" || tag === "SELECT") return;
      const submitBtn = e.currentTarget.querySelector(
        "#submit-button, button[name='submit_btn'], button[type='submit']",
      ) as HTMLButtonElement | null;
      if (submitBtn?.disabled) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      void submit(e);
    },
  };
}

/**
 * Registers a form for INP-safe submit (no native submit event → GTM #document.onsubmit).
 * Pair with SubmitButton (type="button") or onInpSafeButtonClick().
 */
export function useInpSafeForm(submit: InpSafeSubmit) {
  const submitRef = useRef(submit);
  submitRef.current = submit;

  const runSubmit = useCallback(
    (e?: FormEvent | MouseEvent | KeyboardEvent | HTMLElement) => {
      void submitRef.current(e);
    },
    [],
  );

  const formRef = useCallback((el: HTMLFormElement | null) => {
    registerInpSafeFormSubmit(el, (eventOrElement) => {
      runSubmit(eventOrElement);
    });
  }, [runSubmit]);

  const formProps = useMemo(
    () => inpSafeFormProps(runSubmit),
    [runSubmit],
  );

  return { formRef, formProps, runSubmit };
}
